import IUnityTransport from './IUnityTransport';
import UCWindow from '../interfaces/UCWindow';
import { UNITY_EDITOR_URL } from './unityEditorMode';

export type TUnityEditorStatus = 'connecting' | 'connected' | 'disconnected';

export interface IUnityEditorState {
    status: TUnityEditorStatus;
    generation: number;
    lastError: string | null;
}

type TPendingCall = { method: string; arg?: string };

const DEBOUNCE_MS = 150;

class UnityEditorTransport implements IUnityTransport {

    private readonly onState: (state: IUnityEditorState) => void;
    private state: IUnityEditorState = { status: 'connecting', generation: 0, lastError: null };

    private source: EventSource | null = null;
    private opens = 0;

    private queue: TPendingCall[] = [];
    private timer: number | null = null;
    private chain: Promise<void> = Promise.resolve();
    private lastSent = new Map<string, string | undefined>();

    private pendingPositions: string | null = null;
    private positionsFrame = 0;

    private disposed = false;

    constructor(onState: (state: IUnityEditorState) => void) {
        this.onState = onState;
        this.connect();
    }

    public SendMessage(_gameObjectName: string, methodName: string, message?: string): void {
        if (this.disposed) return;

        if (this.lastSent.has(methodName) && this.lastSent.get(methodName) === message) return;
        this.lastSent.set(methodName, message);

        const queued = this.queue.find((call) => call.method === methodName);
        if (queued) {
            queued.arg = message;
        } else {
            this.queue.push({ method: methodName, arg: message });
        }

        if (this.timer !== null) window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.flush(), DEBOUNCE_MS);
    }

    public dispose(): void {
        this.disposed = true;
        if (this.timer !== null) window.clearTimeout(this.timer);
        if (this.positionsFrame) window.cancelAnimationFrame(this.positionsFrame);
        this.source?.close();
        this.source = null;
    }

    // ===== Output =====
    private flush(): void {
        this.timer = null;
        const batch = this.queue;
        this.queue = [];
        if (batch.length === 0) return;
        // Láncolva, hogy egy közben érkező köteg ne előzhesse meg az előzőt.
        this.chain = this.chain.then(async () => {
            for (const call of batch) {
                if (this.disposed) return;
                await this.post(call);
            }
        });
    }

    private async post(call: TPendingCall): Promise<void> {
        try {
            const response = await fetch(`${UNITY_EDITOR_URL}/send`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ method: call.method, arg: call.arg }),
            });
            const result = await response.json();
            if (!result.ok) {
                console.error(`[unity-bridge] ${call.method}:`, result.error, result.log ?? []);
                this.emit({ lastError: `${call.method}: ${result.error}` });
            } else if (result.log?.length) {
                console.log(`[unity-bridge] ${call.method}:`, result.log);
            }
        } catch (error) {
            console.error(`[unity-bridge] ${call.method} nem ért célba:`, error);
            this.emit({ lastError: `${call.method}: nem ért célba` });
        }
    }

    // ===== bejövő =====
    private connect(): void {
        const source = new EventSource(`${UNITY_EDITOR_URL}/events`);
        this.source = source;

        source.onopen = () => {
            this.opens += 1;
            if (this.opens > 1) {
                this.lastSent.clear();
                this.emit({ status: 'connected', generation: this.state.generation + 1, lastError: null });
            } else {
                this.emit({ status: 'connected', lastError: null });
            }
        };

        source.onerror = () => this.emit({ status: 'disconnected' });

        const target = window as UCWindow;
        source.addEventListener('ucLoaded', () => target.UCOnLoadedReceived?.());
        source.addEventListener('ucOrder', (e) => target.UCOrderReceived?.((e as MessageEvent).data));
        source.addEventListener('ucSelected', (e) => target.UCSelectedToBrowserReceived?.((e as MessageEvent).data));
        source.addEventListener('ucDimensionLabels', (e) => target.UCDimensionLabelsReceived?.((e as MessageEvent).data));

        source.addEventListener('ucViewChanged', (e) => {
            try {
                const { key, value } = JSON.parse((e as MessageEvent).data);
                target.UCViewChangedReceived?.(key, value);
            } catch (error) {
                console.error('[unity-bridge] hibás ucViewChanged payload:', error);
            }
        });

        source.addEventListener('ucDimensionPositions', (e) => this.queuePositions((e as MessageEvent).data));
        source.addEventListener('unityLog', (e) => {
            try {
                const entry = JSON.parse((e as MessageEvent).data);
                const log = entry.type === 'Error' || entry.type === 'Exception' ? console.error : console.warn;
                log(`[unity] ${entry.message}`, entry.stackTrace ?? '');
            } catch {
                console.warn('[unity]', (e as MessageEvent).data);
            }
        });
    }

    private queuePositions(payload: string): void {
        this.pendingPositions = payload;
        if (this.positionsFrame) return;
        this.positionsFrame = window.requestAnimationFrame(() => {
            this.positionsFrame = 0;
            const latest = this.pendingPositions;
            this.pendingPositions = null;
            if (latest !== null) (window as UCWindow).UCDimensionPositionsReceived?.(latest);
        });
    }

    private emit(patch: Partial<IUnityEditorState>): void {
        this.state = { ...this.state, ...patch };
        this.onState(this.state);
    }
}

export default UnityEditorTransport;
